/**
 * Content Studio Event Bus — cross-component communication for Maestro→Content Studio live sync.
 *
 * When Maestro triggers content-studio actions via tool calls, the frontend emits events
 * so the Content Studio workspace UI can refresh immediately instead of waiting for the
 * next poll cycle (3-5 seconds).
 *
 * Backed by the typed event bus in `src/shared/lib/events/`. Migrated from
 * raw `window.dispatchEvent(new CustomEvent(...))` in Phase 7 commit 68.
 * The public `emitContentStudioEvent` / `onContentStudioEvent` API is
 * unchanged — only internals swap.
 */

import { createEventBus } from "@/shared/lib/events";

export type ContentStudioEventType =
  | 'articles_changed'
  | 'sources_changed'
  | 'pipeline_started'
  | 'pipeline_completed'
  | 'article_published'
  | 'apply_filters'
  | 'open_source_wizard'
  | 'open_flow_wizard'
  | 'open_flow_detail'
  | 'open_quick_add'
  | 'set_view_mode'
  | 'filter_sources';

export interface ContentStudioEventDetail {
  workspaceId?: string;
  articleId?: string;
  sourceId?: string;
  flowId?: string;
  url?: string;
  /** Filter fields — used by apply_filters event */
  statusGroup?: string;
  search?: string;
  /** View mode — used by set_view_mode event */
  viewMode?: string;
  /** Source filters — used by filter_sources event */
  typeFilter?: string;
  statusFilter?: string;
}

interface ContentStudioBusEvent {
  type: ContentStudioEventType;
  detail: ContentStudioEventDetail;
}

// Module-level singleton — same scope as the previous window.dispatchEvent
// implementation (app-wide). Consumers across content-studio components
// share the same bus instance.
const bus = createEventBus<ContentStudioBusEvent>();

export function emitContentStudioEvent(
  type: ContentStudioEventType,
  detail: ContentStudioEventDetail = {},
): void {
  bus.publish({ type, detail });
}

export function onContentStudioEvent(
  type: ContentStudioEventType,
  handler: (detail: ContentStudioEventDetail) => void,
): () => void {
  return bus.subscribe(type, (event) => handler(event.detail));
}
